import { SIMAElementClassifier, } from "./types";
/**
 * Serializes an ISIMAFormat object into a SIMA formatted text string.
 *
 * @param simaInfo ISIMAFormat data structure
 * @param options Export options including optional siteName override, lineEnding, and comment options
 * @returns Formatted SIMA string
 */
export function exportSIMA(simaInfo, options) {
    const eol = options?.lineEnding || "\n";
    const siteName = options?.siteName ?? simaInfo.fileAttributeData.site_name;
    const header = prepareHeader(simaInfo, siteName, eol, options?.defaultComment);
    const surveyPoints = prepareSurveyPoints(simaInfo.surveyPoints, eol);
    const simaRoutes = prepareRoutes(simaInfo.simaRoutes, eol);
    const parcels = prepareParcels(simaInfo.parcels, eol);
    const triangulations = prepareTriangulations(simaInfo.triangulations, eol);
    const profiles = prepareProfiles(simaInfo.profiles, eol);
    const crossSections = prepareCrossSections(simaInfo.crossSections, eol);
    const sections = [];
    if (header)
        sections.push(header);
    if (surveyPoints)
        sections.push(surveyPoints);
    if (simaRoutes)
        sections.push(simaRoutes);
    if (parcels)
        sections.push(parcels);
    if (triangulations)
        sections.push(triangulations);
    if (profiles)
        sections.push(profiles);
    if (crossSections)
        sections.push(crossSections);
    if (sections.length === 0)
        return "";
    const content = sections.join(eol);
    const appendTrailing = options?.trailingLineEnding ?? true;
    return appendTrailing ? content + eol : content;
}
/**
 * Prepares the header section (Z00, Z01, G00)
 */
function prepareHeader(simaInfo, siteName, eol, defaultComment) {
    let dataString = "";
    if (simaInfo.comment?.text) {
        dataString += `${SIMAElementClassifier.COMMENT}, ${simaInfo.comment.text},${eol}`;
    }
    else if (defaultComment !== false) {
        if (typeof defaultComment === "string") {
            dataString += `${SIMAElementClassifier.COMMENT}, ${defaultComment},${eol}`;
        }
        else {
            const currentDate = new Date();
            const formattedDate = `${currentDate.getFullYear()}${(`0${currentDate.getMonth() + 1}`).slice(-2)}${(`0${currentDate.getDate()}`).slice(-2)}`;
            dataString += `${SIMAElementClassifier.COMMENT}, /* SC_DESIGN: ${formattedDate} */,${eol}`;
        }
    }
    dataString += `Z01,0,${eol}`;
    const versionStr = simaInfo.fileAttributeData.version
        .toString()
        .padStart(2, "0");
    dataString += `${SIMAElementClassifier.FILE_ATTRIBUTE},${versionStr},${siteName},`;
    return dataString;
}
/**
 * Handles A00, A01 and A99 survey points section
 */
function prepareSurveyPoints(surveyCoords, eol) {
    if (!surveyCoords || surveyCoords.length === 0) {
        return "";
    }
    let coordinateDataString = `${SIMAElementClassifier.SURVEY_POINT_BEGIN},${eol}`;
    for (const coord of surveyCoords) {
        coordinateDataString += formatElement(coord, eol);
    }
    coordinateDataString += `${SIMAElementClassifier.SURVEY_POINT_END},`;
    return coordinateDataString;
}
/**
 * Handles F00..F99 route section
 */
function prepareRoutes(routes, eol) {
    if (!routes || routes.length === 0) {
        return "";
    }
    let dataString = "";
    for (let i = 0; i < routes.length; ++i) {
        dataString += prepareRoute(routes[i], eol);
        if (i < routes.length - 1) {
            dataString += eol;
        }
    }
    return dataString;
}
function prepareRoute(route, eol) {
    let dataString = "";
    for (const routeAttribute of route.routeAttributes) {
        if (routeAttribute.classifier === SIMAElementClassifier.ROUTE_BEGIN) {
            dataString += `${SIMAElementClassifier.ROUTE_BEGIN},`;
            dataString += `${routeAttribute.route_number},`;
            dataString += `${routeAttribute.group_number},`;
            dataString += `${routeAttribute.route_name},`;
            dataString += `${routeAttribute.route_type},${eol}`;
        }
        else if (routeAttribute.classifier === SIMAElementClassifier.ROUTE_ATTRIBUTES) {
            dataString += `${SIMAElementClassifier.ROUTE_ATTRIBUTES},`;
            dataString += `${routeAttribute.additional_distance}`;
            if (routeAttribute.station_interval !== undefined) {
                dataString += `,${routeAttribute.station_interval}`;
                if (routeAttribute.station_intermediate_interval !== undefined) {
                    dataString += `,${routeAttribute.station_intermediate_interval}`;
                }
            }
            dataString += `,${eol}`;
        }
    }
    for (const element of route.routeElements) {
        dataString += formatElement(element, eol);
    }
    dataString += `${SIMAElementClassifier.ROUTE_END},`;
    return dataString;
}
/**
 * Handles D00..D99 Cadastral Parcel section
 */
function prepareParcels(parcels, eol) {
    if (!parcels || parcels.length === 0) {
        return "";
    }
    let dataString = "";
    for (let i = 0; i < parcels.length; ++i) {
        const p = parcels[i];
        dataString += `${SIMAElementClassifier.PARCEL_BEGIN},${p.begin.parcel_number},${p.begin.lot_number},${p.begin.parcel_type}`;
        if (p.begin.parcel_name) {
            dataString += `,${p.begin.parcel_name}`;
        }
        dataString += `,${eol}`;
        for (const pt of p.points) {
            dataString += `${pt.classifier},${pt.point_number},${pt.point_name},${eol}`;
        }
        dataString += `${SIMAElementClassifier.PARCEL_END},`;
        if (i < parcels.length - 1) {
            dataString += eol;
        }
    }
    return dataString;
}
/**
 * Handles E00..E99 Triangulation section
 */
function prepareTriangulations(triangulations, eol) {
    if (!triangulations || triangulations.length === 0) {
        return "";
    }
    let dataString = "";
    for (let i = 0; i < triangulations.length; ++i) {
        const b = triangulations[i];
        dataString += `${SIMAElementClassifier.TRIANGULATION_BEGIN},${b.begin.block_number},${b.begin.block_name}`;
        if (b.begin.lot_number !== undefined) {
            dataString += `,${b.begin.lot_number}`;
        }
        dataString += `,${eol}`;
        for (let tIdx = 0; tIdx < b.triangles.length; ++tIdx) {
            const tri = b.triangles[tIdx];
            const tNum = tri.vertex.triangle_number ?? tIdx + 1;
            dataString += `${SIMAElementClassifier.TRIANGULATION_VERTEX},${tNum},${tri.vertex.ptA_number},${tri.vertex.ptA_name},${tri.vertex.ptB_number},${tri.vertex.ptB_name},${tri.vertex.ptC_number},${tri.vertex.ptC_name},${eol}`;
            if (tri.dimension) {
                dataString += `${SIMAElementClassifier.TRIANGULATION_DIMENSION},${tNum},${tri.dimension.side_ab},${tri.dimension.side_bc},${tri.dimension.side_ca},${tri.dimension.base},${tri.dimension.height},${eol}`;
            }
        }
        dataString += `${SIMAElementClassifier.TRIANGULATION_END},`;
        if (i < triangulations.length - 1) {
            dataString += eol;
        }
    }
    return dataString;
}
/**
 * Handles H00..H99 Longitudinal Profile section
 */
function prepareProfiles(profiles, eol) {
    if (!profiles || profiles.length === 0) {
        return "";
    }
    let dataString = "";
    for (let i = 0; i < profiles.length; ++i) {
        const p = profiles[i];
        dataString += `${SIMAElementClassifier.PROFILE_BEGIN},${p.alignment_number},${p.alignment_name},${eol}`;
        for (const st of p.stations) {
            dataString += `${SIMAElementClassifier.PROFILE_POINT},${st.point_name},${st.chainage},${st.ground_elevation},${st.stake_elevation},${st.point_type},${eol}`;
        }
        dataString += `${SIMAElementClassifier.PROFILE_END},`;
        if (i < profiles.length - 1) {
            dataString += eol;
        }
    }
    return dataString;
}
/**
 * Handles I00..I99 Cross Section section
 */
function prepareCrossSections(crossSections, eol) {
    if (!crossSections || crossSections.length === 0) {
        return "";
    }
    let dataString = "";
    for (let i = 0; i < crossSections.length; ++i) {
        const cs = crossSections[i];
        dataString += `${SIMAElementClassifier.CROSS_SECTION_BEGIN},${cs.alignment_number},${cs.alignment_name},${eol}`;
        for (const st of cs.stations) {
            dataString += `${SIMAElementClassifier.CROSS_SECTION_CENTER},${st.center.section_name},${st.center.chainage},${st.center.ground_elevation},${st.center.stake_elevation},${eol}`;
            for (const tp of st.transversePoints) {
                dataString += `${SIMAElementClassifier.CROSS_SECTION_POINT},${tp.point_name},${tp.offset_distance},${tp.ground_elevation},${eol}`;
            }
        }
        dataString += `${SIMAElementClassifier.CROSS_SECTION_END},`;
        if (i < crossSections.length - 1) {
            dataString += eol;
        }
    }
    return dataString;
}
function formatElement(element, eol) {
    let dataString = "";
    if ("rawLine" in element && element.rawLine) {
        return `${element.rawLine}${eol}`;
    }
    switch (element.classifier) {
        case SIMAElementClassifier.FILE_ATTRIBUTE:
            {
                const fileAttribute = element;
                dataString += `${fileAttribute.classifier},`;
                dataString += `${fileAttribute.version.toString().padStart(2, "0")},`;
                dataString += `${fileAttribute.site_name},${eol}`;
            }
            break;
        case SIMAElementClassifier.SURVEY_POINT_COORD:
            {
                const surveyPointCoord = element;
                dataString += `${surveyPointCoord.classifier},`;
                dataString += `${surveyPointCoord.point_number},`;
                dataString += `${surveyPointCoord.point_name},`;
                dataString += `${surveyPointCoord.x},`;
                dataString += `${surveyPointCoord.y},`;
                dataString += `${surveyPointCoord.z},${eol}`;
            }
            break;
        case SIMAElementClassifier.ROUTE_BEGIN:
            {
                const routeBegin = element;
                dataString += `${routeBegin.classifier},`;
                dataString += `${routeBegin.route_number},`;
                dataString += `${routeBegin.group_number},`;
                dataString += `${routeBegin.route_name},`;
                dataString += `${routeBegin.route_type},${eol}`;
            }
            break;
        case SIMAElementClassifier.ROUTE_COORD:
        case SIMAElementClassifier.PARCEL_POINT:
            {
                const routeCoord = element;
                dataString += `${routeCoord.classifier},`;
                dataString += `${routeCoord.point_number},`;
                dataString += `${routeCoord.point_name},${eol}`;
            }
            break;
        case SIMAElementClassifier.ROUTE_COORD_ADDITIONAL_DISTANCE:
            {
                const routeCoordAdditionalDistance = element;
                dataString += `${routeCoordAdditionalDistance.classifier},`;
                dataString += `${routeCoordAdditionalDistance.additional_distance},${eol}`;
            }
            break;
        case SIMAElementClassifier.SIMPLE_CURVE:
            {
                const simpleCurve = element;
                dataString += `${simpleCurve.classifier},`;
                dataString += `${simpleCurve.direction},`;
                dataString += `${simpleCurve.radius},${eol}`;
            }
            break;
        case SIMAElementClassifier.CLOTHOID_CURVE:
            {
                const clothoidCurve = element;
                dataString += `${clothoidCurve.classifier},`;
                dataString += `${clothoidCurve.parameterA},`;
                dataString += `${clothoidCurve.start_radius},`;
                dataString += `${clothoidCurve.end_radius},${eol}`;
            }
            break;
        default:
            {
                dataString += `${element.classifier},${eol}`;
            }
            break;
    }
    return dataString;
}
//# sourceMappingURL=exporter.js.map